'use client';
import React, { useState } from 'react';
import { useSiteSettings } from '@/components/SiteSettingsProvider';
import { usePathname } from 'next/navigation';
import { Mail, Phone, MapPin, ChevronRight, Send, ShieldCheck } from 'lucide-react';

interface FooterProps {
  onDestinationClick?: (cityName: string) => void;
}

export default function Footer({ onDestinationClick }: FooterProps) {
  const { phoneNumber: phone, showPhoneNumber } = useSiteSettings();
  const pathname = usePathname();
  const isContactPage = pathname === '/contact-us';
  const shouldShowPhone = showPhoneNumber || isContactPage;

  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    setSubscribed(false);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe.');
      }

      setSubscribed(true);
      setEmail('');
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Premium Top Border Accent Line */}
      <div className="h-1 w-full bg-gradient-to-r from-accent via-blue-500 to-brand-slate" />

      <footer className="bg-slate-50 text-slate-600 pt-20 pb-12 relative overflow-hidden font-sans border-t border-slate-200/60">
        {/* Subtle Decorative Ambient Lighting for Light Theme */}
        <div className="absolute bottom-[-100px] left-[-100px] w-96 h-96 bg-accent/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-[1170px] mx-auto px-6 relative z-10">
          {/* Top Logo & Description Section */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-12 border-b border-slate-200/80 mb-12">
            <div className="max-w-md text-left">
              {/* Note: Standard dark version logo is used to contrast beautifully against light bg */}
              <img
                src="/assets/images/logo/color1/full/color1_logo_transparent_background.png"
                alt="Faremaps"
                className="h-9 w-auto object-contain mb-4"
              />
              <p className="text-slate-500 text-xs md:text-sm font-semibold leading-relaxed">
                Faremaps is your trusted gateway to premium private travel assets and unpublished
                flight deals. Experience seamless bookings and dedicated 24/7 expert support.
              </p>
            </div>

            {/* VIP Support Banner */}
            <div className="bg-white border border-slate-200/60 rounded-2xl p-4 flex items-center gap-4 text-left shadow-sm backdrop-blur-md">
              <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
                <ShieldCheck className="w-6 h-6 animate-pulseGlow" />
              </div>
              <div>
                <div className="text-[10px] font-black text-accent uppercase tracking-widest leading-none">
                  SECURE BOOKINGS
                </div>
                <div className="text-xs font-bold text-slate-800 mt-1">
                  100% Verified Ticketing Guarantee
                </div>
              </div>
            </div>
          </div>

          {/* Footer Grid Columns */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {/* Column 1: Quick Links */}
            <div className="text-left">
              <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-6 pb-2 border-b border-slate-200/80 inline-block">
                Quick Links
              </h4>
              <ul className="space-y-3">
                {[
                  { name: 'About Us', href: '/about-us' },
                  { name: 'Contact Us', href: '/contact-us' },
                  { name: 'Privacy Policy', href: '/privacy-policy' },
                  { name: 'Terms & Conditions', href: '/terms-conditions' },
                  { name: 'Refund Policy', href: '/refund-policy' },
                  { name: 'Disclaimer', href: '/disclaimer' },
                  { name: 'Cancellation Policy', href: '/cancellation-policy' },
                ].map((link) => (
                  <li key={link.name} className="group">
                    <a
                      href={link.href}
                      className="text-slate-500 hover:text-accent text-xs font-extrabold flex items-center gap-1.5 transition-colors duration-300"
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: Top Destinations */}
            <div className="text-left">
              <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-6 pb-2 border-b border-slate-200/80 inline-block">
                Top Destinations
              </h4>
              <ul className="space-y-3">
                {[
                  'Flights to New York',
                  'Flights to Los Angeles',
                  'Flights to Miami',
                  'Flights to Las Vegas',
                  'Flights to London',
                  'Flights to Paris',
                  'Flights to Cancun',
                  'Flights to Dubai',
                ].map((dest) => {
                  const cityName = dest.replace('Flights to ', '');
                  return (
                    <li key={dest} className="group">
                      <button
                        type="button"
                        onClick={() => {
                          if (onDestinationClick) {
                            onDestinationClick(cityName);
                          }
                        }}
                        className="text-slate-500 hover:text-accent text-xs font-extrabold flex items-center gap-1.5 transition-colors duration-300 cursor-pointer"
                      >
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-accent group-hover:translate-x-0.5 transition-all" />
                        {dest}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Column 3: Reach Us & Call Desk */}
            <div className="text-left flex flex-col">
              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-6 pb-2 border-b border-slate-200/80 inline-block">
                  Reach Us
                </h4>
                <div className="flex gap-2.5 items-start text-xs text-slate-500 font-extrabold mb-4">
                  <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>13900 NE 12th St Bellevue, WA 98005, USA</span>
                </div>
                <div className="flex gap-2.5 items-center text-xs text-slate-500 font-extrabold mb-6">
                  <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                  <a
                    href="mailto:support@faremaps.com"
                    className="hover:text-accent transition-colors"
                  >
                    support@faremaps.com
                  </a>
                </div>
              </div>

              {/* VIP Dedicated Desk Card */}
              {shouldShowPhone && (
                <div className="bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm text-left backdrop-blur-sm">
                  <div className="flex items-center gap-2 mt-2">
                    <a
                      href={`tel:${phone}`}
                      className="text-slate-800 font-black text-lg tracking-tight hover:text-accent transition-colors leading-none"
                    >
                      {phone}
                    </a>
                  </div>
                  <span className="text-[8.5px] text-slate-400 font-extrabold uppercase tracking-wider block mt-1.5">
                    TOLL-FREE • 24/7 SUPPORT
                  </span>
                </div>
              )}
            </div>

            {/* Column 4: Newsletter & Payments */}
            <div className="text-left">
              <div className="bg-white border border-slate-200/60 p-5 rounded-2xl shadow-sm backdrop-blur-md mb-6">
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-1.5">
                  Planning Your Trip?
                </h4>
                <p className="text-slate-500 text-[10.5px] font-semibold leading-normal mb-4">
                  Subscribe to receive priority notifications on unpublished private deals.
                </p>
                <form onSubmit={handleSubscribe} className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 min-w-0 h-9 px-3 text-xs bg-slate-50 border border-slate-200/80 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-accent/40 focus:bg-white transition-all"
                    required
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-accent hover:bg-accent-dark text-white font-bold px-3.5 rounded-lg text-xs transition-colors flex items-center justify-center shadow-sm active:scale-95 flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-label="Subscribe"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </form>
                {error && (
                  <p className="text-rose-600 text-[9.5px] font-black mt-2 animate-fadeInUp">
                    ✗ {error}
                  </p>
                )}
                {subscribed && (
                  <p className="text-emerald-600 text-[10px] font-black mt-2 animate-fadeInUp">
                    ✓ Welcome to priority travel access!
                  </p>
                )}
              </div>

              {/* Payment Methods & Business Hours */}
              <div className="flex flex-col gap-4">
                <div className="text-[10px] text-slate-500 font-extrabold uppercase tracking-wider">
                  <strong>Business Hours:</strong> 8AM to 11PM (EST)
                </div>
                <div>
                  <h4 className="text-slate-400 text-[8.5px] font-black uppercase tracking-widest mb-2">
                    Accepted Payment Options
                  </h4>
                  <div className="grid grid-cols-4 gap-x-3 gap-y-2 w-[150px] mt-1.5 items-center justify-items-start">
                    {[
                      {
                        name: 'Visa',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/visa.svg',
                      },
                      {
                        name: 'Mastercard',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/mastercard.svg',
                      },
                      {
                        name: 'Amex',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/amex.svg',
                      },
                      {
                        name: 'Discover',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/discover.svg',
                      },
                      {
                        name: 'PayPal',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/paypal.svg',
                      },
                      {
                        name: 'JCB',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/jcb.svg',
                      },
                      {
                        name: 'Diners Club',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/diners.svg',
                      },
                      {
                        name: 'UnionPay',
                        logo: 'https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/unionpay.svg',
                      },
                    ].map((card) => (
                      <img
                        key={card.name}
                        src={card.logo}
                        alt={card.name}
                        className="h-5 w-auto object-contain flex-shrink-0 hover:scale-105 transition-transform duration-300"
                        title={card.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Icons Media Grid */}
          <div className="flex justify-between items-center flex-wrap gap-4 pt-10 mt-12 border-t border-slate-200/80">
            <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-widest">
              © FAREMAPS INC. ALL RIGHTS RESERVED.
            </span>

            {/* Social squircle icons */}
            <ul className="flex gap-3">
              {[
                {
                  label: 'Facebook',
                  color: 'hover:bg-[#1877F2]',
                  url: 'https://m.facebook.com/bookfaremaps/',
                  path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
                },
                {
                  label: 'Instagram',
                  color: 'hover:bg-[#E1306C]',
                  url: 'https://instagram.com/faremaps',
                  path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
                },
                {
                  label: 'Twitter',
                  color: 'hover:bg-[#1DA1F2]',
                  url: 'https://x.com/faremaps',
                  path: 'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z',
                },
                {
                  label: 'LinkedIn',
                  color: 'hover:bg-[#0A66C2]',
                  url: 'https://www.linkedin.com/company/faremaps/',
                  path: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z',
                },

                {
                  label: 'YouTube',
                  color: 'hover:bg-[#FF0000]',
                  url: 'https://youtube.com/@faremaps',
                  path: 'M23.498 6.186a2.997 2.997 0 00-2.11-2.12C19.505 3.5 12 3.5 12 3.5s-7.505 0-9.388.566a2.997 2.997 0 00-2.11 2.12A31.16 31.16 0 000 12a31.16 31.16 0 00.502 5.814 2.997 2.997 0 002.11 2.12C4.495 20.5 12 20.5 12 20.5s7.505 0 9.388-.566a2.997 2.997 0 002.11-2.12A31.16 31.16 0 0024 12a31.16 31.16 0 00-.502-5.814zM9.75 15.568V8.432L15.818 12 9.75 15.568z',
                },
              ].map((social) => (
                <li key={social.label}>
                  <a
                    href={social.url}
                    target={social.url.startsWith('http') ? '_blank' : undefined}
                    rel={social.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className={`flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-slate-200 text-slate-400 hover:text-white ${social.color} transition-all duration-300 hover:-translate-y-0.5 shadow-sm`}
                    aria-label={social.label}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d={social.path} />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>

      {/* Premium legal disclaimer section */}
      <div className="bg-slate-100 py-6 border-t border-slate-200/60">
        <div className="max-w-[1170px] mx-auto px-6">
          <p className="text-[10px] text-slate-500 font-semibold leading-relaxed text-left">
            <strong>Disclaimer:</strong> Faremaps.com solely acts as an agent, creating a bridge
            between travelers and suppliers of travel services including flight tickets and other
            travel services. We do not own or manage any kind of Travel Services. All prices shown
            are for round trip tickets unless otherwise stated. Displayed fares are based on
            availability and are subject to change. The discount offer is applicable to phone
            bookings.
          </p>
        </div>
      </div>
    </>
  );
}
